import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Filter, Download, BarChart3, Building2, FileText, Calendar } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';


const DrillDownAnalyticsModal = ({ isOpen, onClose }) => {
  const [selectedMetric, setSelectedMetric] = useState('conversion');
  const [timeRange, setTimeRange] = useState('6months');
  const [selectedDealership, setSelectedDealership] = useState('all');
  const [drillLevel, setDrillLevel] = useState(1);
  const [loading, setLoading] = useState(true);
  
  // Real data states
  const [conversionDetailData, setConversionDetailData] = useState([]);
  const [dealershipComparisonData, setDealershipComparisonData] = useState([]);
  const [voucherMetricsData, setVoucherMetricsData] = useState([]);
  const [temporalAnalysisData, setTemporalAnalysisData] = useState([]);
  const [dealerships, setDealerships] = useState([{ value: 'all', label: 'Alle Autohäuser' }]);
  const [summaryStats, setSummaryStats] = useState({});

  const metrics = [
    { value: 'conversion', label: 'Conversion-Raten', icon: TrendingUp },
    { value: 'dealership', label: 'Dealership-Performance', icon: Building2 },
    { value: 'voucher', label: 'Voucher-Metriken', icon: FileText },
    { value: 'temporal', label: 'Zeitliche Analyse', icon: Calendar }
  ];

  const timeRanges = [
    { value: '1month', label: 'Letzter Monat' },
    { value: '3months', label: 'Letzte 3 Monate' },
    { value: '6months', label: 'Letzte 6 Monate' },
    { value: '1year', label: 'Letztes Jahr' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchAnalyticsData();
    }
  }, [isOpen, timeRange, selectedDealership]);

  const getDateRangeFilter = () => {
    const now = new Date();
    let startDate;
    
    switch(timeRange) {
      case '1month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case '3months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case '6months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case '1year':
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    }
    
    return startDate?.toISOString();
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      let startDate = getDateRangeFilter();

      // Build query with optional dealership filter
      let applicationsQuery = supabase?.from('applications')?.select(`
          *,
          dealerships (
            id,
            name,
            city
          )
        `)?.gte('application_date', startDate)?.order('application_date', { ascending: true });

      if (selectedDealership !== 'all') {
        applicationsQuery = applicationsQuery?.eq('dealership_id', selectedDealership);
      }

      const { data: applications, error: appsError } = await applicationsQuery;
      if (appsError) throw appsError;

      // Fetch vouchers
      let vouchersQuery = supabase?.from('vouchers')?.select('*')?.gte('created_at', startDate);

      if (selectedDealership !== 'all') {
        vouchersQuery = vouchersQuery?.eq('dealership_id', selectedDealership);
      }

      const { data: vouchers, error: vouchersError } = await vouchersQuery;
      if (vouchersError) throw vouchersError;

      // Fetch all dealerships for dropdown
      const { data: dealershipsData, error: dealershipsError } = await supabase?.from('dealerships')?.select('id, name')?.eq('status', 'active')?.order('name');

      if (dealershipsError) throw dealershipsError;

      // Update dealerships dropdown
      const dealershipOptions = [
        { value: 'all', label: 'Alle Autohäuser' },
        ...(dealershipsData?.map(d => ({ value: d?.id, label: d?.name })) || [])
      ];
      setDealerships(dealershipOptions);

      // Process conversion data by month
      const conversionByMonth = processConversionData(applications);
      setConversionDetailData(conversionByMonth);

      // Process dealership comparison
      const dealershipStats = processDealershipData(applications);
      setDealershipComparisonData(dealershipStats);

      // Process voucher metrics
      const voucherStats = processVoucherData(vouchers);
      setVoucherMetricsData(voucherStats);

      // Process temporal analysis (by day of week)
      const temporalStats = processTemporalData(applications);
      setTemporalAnalysisData(temporalStats);

      // Calculate summary statistics
      const summary = calculateSummaryStats(applications, vouchers, dealershipStats);
      setSummaryStats(summary);

    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processConversionData = (applications) => {
    const monthlyData = {};
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

    applications?.forEach(app => {
      const date = new Date(app?.application_date);
      const monthKey = `${date?.getFullYear()}-${date?.getMonth()}`;
      const monthLabel = monthNames?.[date?.getMonth()];

      if (!monthlyData?.[monthKey]) {
        monthlyData[monthKey] = {
          month: monthLabel,
          submitted: 0,
          approved: 0,
          rejected: 0,
          pending: 0,
          processing: 0,
          rate: 0
        };
      }

      monthlyData[monthKey].submitted += 1;
      
      if (app?.application_status === 'approved') {
        monthlyData[monthKey].approved += 1;
      } else if (app?.application_status === 'rejected') {
        monthlyData[monthKey].rejected += 1;
      } else if (app?.application_status === 'pending') {
        monthlyData[monthKey].pending += 1;
      } else if (app?.application_status === 'processing') {
        monthlyData[monthKey].processing += 1;
      }
    });

    // Calculate rates
    Object.keys(monthlyData)?.forEach(key => {
      const data = monthlyData?.[key];
      if (data?.submitted > 0) {
        data.rate = parseFloat(((data?.approved / data?.submitted) * 100)?.toFixed(1));
      }
    });

    return Object.values(monthlyData);
  };

  const processDealershipData = (applications) => {
    const dealershipStats = {};

    applications?.forEach(app => {
      const dealershipId = app?.dealership_id;
      const dealershipName = app?.dealerships?.name || 'Unbekannt';
      const dealershipCity = app?.dealerships?.city || '';

      if (!dealershipStats?.[dealershipId]) {
        dealershipStats[dealershipId] = {
          name: dealershipCity || dealershipName,
          applications: 0,
          approved: 0,
          processingTimes: [],
          conversionRate: 0,
          revenue: 0
        };
      }

      dealershipStats[dealershipId].applications += 1;

      if (app?.application_status === 'approved') {
        dealershipStats[dealershipId].approved += 1;
        
        // Calculate revenue (assuming premium_amount exists)
        if (app?.premium_amount) {
          dealershipStats[dealershipId].revenue += parseFloat(app?.premium_amount);
        }

        // Calculate processing time
        if (app?.approval_date) {
          const submitDate = new Date(app?.application_date);
          const approvalDate = new Date(app?.approval_date);
          const daysDiff = (approvalDate - submitDate) / (1000 * 60 * 60 * 24);
          dealershipStats?.[dealershipId]?.processingTimes?.push(daysDiff);
        }
      }
    });

    // Calculate averages and conversion rates
    const result = Object.values(dealershipStats)?.map(stat => {
      const avgProcessingTime = stat?.processingTimes?.length > 0
        ? stat?.processingTimes?.reduce((a, b) => a + b, 0) / stat?.processingTimes?.length
        : 0;

      const conversionRate = stat?.applications > 0
        ? (stat?.approved / stat?.applications) * 100
        : 0;

      return {
        name: stat?.name,
        applications: stat?.applications,
        approved: stat?.approved,
        avgProcessingTime: parseFloat(avgProcessingTime?.toFixed(1)),
        conversionRate: parseFloat(conversionRate?.toFixed(1)),
        revenue: Math.round(stat?.revenue)
      };
    });

    // Sort by applications count and take top 5
    return result?.sort((a, b) => b?.applications - a?.applications)?.slice(0, 5);
  };

  const processVoucherData = (vouchers) => {
    const stats = {
      generated: 0,
      verified: 0,
      redeemed: 0,
      expired: 0
    };

    vouchers?.forEach(voucher => {
      stats.generated += 1;

      if (voucher?.verification_date) {
        stats.verified += 1;
      }

      if (voucher?.voucher_status === 'used') {
        stats.redeemed += 1;
      } else if (voucher?.voucher_status === 'expired') {
        stats.expired += 1;
      }
    });

    return [
      { category: 'Neu generiert', value: stats?.generated, color: '#3b82f6' },
      { category: 'Verifiziert', value: stats?.verified, color: '#10b981' },
      { category: 'Eingelöst', value: stats?.redeemed, color: '#8b5cf6' },
      { category: 'Abgelaufen', value: stats?.expired, color: '#ef4444' }
    ];
  };

  const processTemporalData = (applications) => {
    const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const dayStats = Array(7)?.fill(null)?.map((_, index) => ({
      time: dayNames?.[index],
      applications: 0,
      approved: 0,
      avgRate: 0
    }));

    applications?.forEach(app => {
      const date = new Date(app?.application_date);
      const dayOfWeek = date?.getDay();

      dayStats[dayOfWeek].applications += 1;

      if (app?.application_status === 'approved') {
        dayStats[dayOfWeek].approved += 1;
      }
    });

    // Calculate average rates
    dayStats?.forEach(day => {
      if (day?.applications > 0) {
        day.avgRate = Math.round((day?.approved / day?.applications) * 100);
      }
    });

    // Reorder to start with Monday
    return [
      dayStats?.[1], // Mo
      dayStats?.[2], // Di
      dayStats?.[3], // Mi
      dayStats?.[4], // Do
      dayStats?.[5], // Fr
      dayStats?.[6], // Sa
      dayStats?.[0]  // So
    ];
  };

  const calculateSummaryStats = (applications, vouchers, dealershipStats) => {
    const totalApplications = applications?.length || 0;
    const approvedCount = applications?.filter(app => app?.application_status === 'approved')?.length || 0;
    const rejectedCount = applications?.filter(app => app?.application_status === 'rejected')?.length || 0;
    
    const avgConversionRate = totalApplications > 0
      ? ((approvedCount / totalApplications) * 100)?.toFixed(1)
      : 0;

    const rejectionRate = totalApplications > 0
      ? ((rejectedCount / totalApplications) * 100)?.toFixed(1)
      : 0;

    // Find best month
    const monthlyRates = conversionDetailData?.map(m => ({ month: m?.month, rate: m?.rate }));
    const bestMonth = monthlyRates?.length > 0
      ? monthlyRates?.reduce((max, current) => current?.rate > max?.rate ? current : max, monthlyRates?.[0])
      : { month: 'N/A', rate: 0 };

    // Dealership stats
    const topPerformer = dealershipStats?.length > 0
      ? dealershipStats?.reduce((max, current) => current?.conversionRate > max?.conversionRate ? current : max, dealershipStats?.[0])
      : { name: 'N/A', conversionRate: 0 };

    const totalRevenue = dealershipStats?.reduce((sum, d) => sum + d?.revenue, 0);
    
    const avgProcessingTime = dealershipStats?.length > 0
      ? (dealershipStats?.reduce((sum, d) => sum + d?.avgProcessingTime, 0) / dealershipStats?.length)?.toFixed(1)
      : 0;

    // Voucher stats
    const voucherGenerated = voucherMetricsData?.find(v => v?.category === 'Neu generiert')?.value || 0;
    const voucherVerified = voucherMetricsData?.find(v => v?.category === 'Verifiziert')?.value || 0;
    const voucherRedeemed = voucherMetricsData?.find(v => v?.category === 'Eingelöst')?.value || 0;
    const voucherExpired = voucherMetricsData?.find(v => v?.category === 'Abgelaufen')?.value || 0;

    const verificationRate = voucherGenerated > 0
      ? ((voucherVerified / voucherGenerated) * 100)?.toFixed(1)
      : 0;

    const redemptionRate = voucherVerified > 0
      ? ((voucherRedeemed / voucherVerified) * 100)?.toFixed(1)
      : 0;

    const expiryRate = voucherGenerated > 0
      ? ((voucherExpired / voucherGenerated) * 100)?.toFixed(1)
      : 0;

    // Temporal stats
    const bestDay = temporalAnalysisData?.length > 0
      ? temporalAnalysisData?.reduce((max, current) => current?.applications > max?.applications ? current : max, temporalAnalysisData?.[0])
      : { time: 'N/A', applications: 0 };

    const weekendTotal = temporalAnalysisData?.length >= 7
      ? temporalAnalysisData?.[5]?.applications + temporalAnalysisData?.[6]?.applications
      : 0;

    return {
      avgConversionRate,
      rejectionRate,
      totalApplications,
      bestMonth,
      topPerformer,
      totalRevenue,
      avgProcessingTime,
      voucherGenerated,
      voucherVerified,
      voucherRedeemed,
      voucherExpired,
      verificationRate,
      redemptionRate,
      expiryRate,
      bestDay,
      weekendTotal
    };
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const renderAnalytics = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Lade Analytics-Daten...</p>
          </div>
        </div>
      );
    }

    switch(selectedMetric) {
      case 'conversion':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-primary/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Durchschnittliche Rate</p>
                <p className="text-2xl font-bold text-primary">{summaryStats?.avgConversionRate || 0}%</p>
                <p className="text-xs text-muted-foreground mt-1">Gesamt-Zeitraum</p>
              </div>
              <div className="bg-success/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Beste Conversion</p>
                <p className="text-2xl font-bold text-success">{summaryStats?.bestMonth?.rate || 0}%</p>
                <p className="text-xs text-muted-foreground mt-1">{summaryStats?.bestMonth?.month || 'N/A'}</p>
              </div>
              <div className="bg-warning/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Gesamt-Anträge</p>
                <p className="text-2xl font-bold text-warning">{summaryStats?.totalApplications || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Im Zeitraum</p>
              </div>
              <div className="bg-destructive/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Ablehnungsrate</p>
                <p className="text-2xl font-bold text-destructive">{summaryStats?.rejectionRate || 0}%</p>
                <p className="text-xs text-muted-foreground mt-1">Gesamt</p>
              </div>
            </div>

            {conversionDetailData?.length > 0 ? (
              <>
                <div className="bg-white border border-border rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Conversion-Trend im Detail</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={conversionDetailData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="submitted" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Eingereicht" />
                      <Area type="monotone" dataKey="approved" stackId="2" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Genehmigt" />
                      <Area type="monotone" dataKey="rejected" stackId="3" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} name="Abgelehnt" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white border border-border rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Monatliche Conversion-Raten</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={conversionDetailData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="rate" stroke="#3b82f6" strokeWidth={3} name="Conversion-Rate %" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="bg-white border border-border rounded-lg p-6 text-center text-muted-foreground">
                Keine Daten für den ausgewählten Zeitraum verfügbar
              </div>
            )}
          </div>
        );

      case 'dealership':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-primary/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Top Performer</p>
                <p className="text-xl font-bold text-primary">{summaryStats?.topPerformer?.name || 'N/A'}</p>
                <p className="text-xs text-success mt-1">{summaryStats?.topPerformer?.conversionRate || 0}% Conversion</p>
              </div>
              <div className="bg-success/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Gesamt-Revenue</p>
                <p className="text-xl font-bold text-success">€{summaryStats?.totalRevenue?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Top Dealerships</p>
              </div>
              <div className="bg-warning/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Ø Bearbeitungszeit</p>
                <p className="text-xl font-bold text-warning">{summaryStats?.avgProcessingTime || 0} Tage</p>
                <p className="text-xs text-muted-foreground mt-1">Durchschnitt</p>
              </div>
            </div>
            
            {dealershipComparisonData?.length > 0 ? (
              <>
                <div className="bg-white border border-border rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Dealership-Vergleich</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dealershipComparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="applications" fill="#3b82f6" name="Anträge" />
                      <Bar dataKey="approved" fill="#10b981" name="Genehmigt" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-border rounded-lg p-6">
                    <h4 className="text-lg font-semibold mb-4">Bearbeitungszeit-Vergleich</h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={dealershipComparisonData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" stroke="#6b7280" />
                        <YAxis dataKey="name" type="category" stroke="#6b7280" />
                        <Tooltip />
                        <Bar dataKey="avgProcessingTime" fill="#f59e0b" name="Tage" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white border border-border rounded-lg p-6">
                    <h4 className="text-lg font-semibold mb-4">Revenue-Verteilung</h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={dealershipComparisonData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="revenue"
                        >
                          {dealershipComparisonData?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `€${value?.toLocaleString()}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white border border-border rounded-lg p-6 text-center text-muted-foreground">
                Keine Dealership-Daten für den ausgewählten Zeitraum verfügbar
              </div>
            )}
          </div>
        );

      case 'voucher':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-primary/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Generiert</p>
                <p className="text-2xl font-bold text-primary">{summaryStats?.voucherGenerated || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Gesamt</p>
              </div>
              <div className="bg-success/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Verifiziert</p>
                <p className="text-2xl font-bold text-success">{summaryStats?.voucherVerified || 0}</p>
                <p className="text-xs text-success mt-1">{summaryStats?.verificationRate || 0}% Rate</p>
              </div>
              <div className="bg-secondary/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Eingelöst</p>
                <p className="text-2xl font-bold text-secondary">{summaryStats?.voucherRedeemed || 0}</p>
                <p className="text-xs text-success mt-1">{summaryStats?.redemptionRate || 0}% von verifiziert</p>
              </div>
              <div className="bg-destructive/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Abgelaufen</p>
                <p className="text-2xl font-bold text-destructive">{summaryStats?.voucherExpired || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">{summaryStats?.expiryRate || 0}% Rate</p>
              </div>
            </div>
            {voucherMetricsData?.length > 0 && voucherMetricsData?.some(v => v?.value > 0) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-border rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Voucher-Status Verteilung</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={voucherMetricsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ category, percent }) => `${category}: ${(percent * 100)?.toFixed(1)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {voucherMetricsData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry?.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white border border-border rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Voucher-Metriken Übersicht</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={voucherMetricsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="category" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip />
                      <Bar dataKey="value" name="Anzahl">
                        {voucherMetricsData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry?.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-border rounded-lg p-6 text-center text-muted-foreground">
                Keine Voucher-Daten für den ausgewählten Zeitraum verfügbar
              </div>
            )}
          </div>
        );

      case 'temporal':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-primary/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Bester Tag</p>
                <p className="text-xl font-bold text-primary">{summaryStats?.bestDay?.time || 'N/A'}</p>
                <p className="text-xs text-success mt-1">{summaryStats?.bestDay?.applications || 0} Anträge</p>
              </div>
              <div className="bg-success/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Durchschnitt/Tag</p>
                <p className="text-xl font-bold text-success">
                  {temporalAnalysisData?.length > 0 
                    ? Math.round(temporalAnalysisData?.reduce((sum, d) => sum + d?.applications, 0) / temporalAnalysisData?.length)
                    : 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Anträge pro Tag</p>
              </div>
              <div className="bg-warning/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Wochenende</p>
                <p className="text-xl font-bold text-warning">{summaryStats?.weekendTotal || 0} Anträge</p>
                <p className="text-xs text-muted-foreground mt-1">Sa + So gesamt</p>
              </div>
            </div>
            {temporalAnalysisData?.length > 0 && temporalAnalysisData?.some(d => d?.applications > 0) ? (
              <>
                <div className="bg-white border border-border rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Wochentag-Analyse</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={temporalAnalysisData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="time" stroke="#6b7280" />
                      <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" />
                      <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="applications" fill="#3b82f6" name="Anträge" />
                      <Bar yAxisId="right" dataKey="avgRate" fill="#10b981" name="Ø Rate %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white border border-border rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Aktivitäts-Trend</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={temporalAnalysisData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="time" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip />
                      <Area type="monotone" dataKey="applications" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Anträge" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="bg-white border border-border rounded-lg p-6 text-center text-muted-foreground">
                Keine zeitlichen Daten für den ausgewählten Zeitraum verfügbar
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const handleExport = () => {
    console.log('Exporting analytics data...');
    alert('Analytics-Daten werden als CSV exportiert...');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground">
                Detaillierte Analytics
              </h2>
              <p className="text-sm text-muted-foreground">
                Tiefgehende Performance-Insights und Drill-Down-Analyse
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-border bg-muted/30 p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex gap-2">
              {metrics?.map((metric) => {
                const MetricIcon = metric?.icon;
                return (
                  <button
                    key={metric?.value}
                    onClick={() => setSelectedMetric(metric?.value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      selectedMetric === metric?.value
                        ? 'bg-primary text-white' :'bg-white text-foreground hover:bg-primary/10'
                    }`}
                  >
                    <MetricIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">{metric?.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex-1" />

            <div className="flex gap-3">
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e?.target?.value)}
                options={timeRanges}
                className="w-48"
              />

              <Select
                value={selectedDealership}
                onChange={(e) => setSelectedDealership(e?.target?.value)}
                options={dealerships}
                className="w-56"
              />

              <Button
                variant="outline"
                onClick={handleExport}
                className="flex items-center gap-2"
                iconName="download"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-220px)] p-6">
          {renderAnalytics()}
        </div>

        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" />
              <span>
                Zeigt Daten für: {timeRanges?.find(t => t?.value === timeRange)?.label} | {dealerships?.find(d => d?.value === selectedDealership)?.label}
              </span>
            </div>
            <Button onClick={onClose} iconName="close">
              Schließen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrillDownAnalyticsModal;